/**
 * Vault security dialogs (Phase 9): show the recovery phrase / keys (password required again,
 * spec §6.4), change the password, add a private key for another chain, remove the vault.
 * Secrets shown here are dropped from memory when the dialog closes.
 */
import { Button, Field, Input, Modal, Notice, Select } from "@custos/ui";
import { exportPrivateKey, KEY_FAMILIES, type KeyFamily, type VaultSecret } from "@custos/vault";
import { useEffect, useState, type FormEvent } from "react";
import { errorMessage } from "../lib/errors";
import { useConfig } from "../lib/queries";
import { revealVaultSecret, useVault } from "../lib/vault";
import { FAMILY_NAME, familyChains, privateKeyHint } from "../lib/vault-ui";
import { NewPasswordFields, SecretInput, WordGrid } from "./VaultBits";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Runs an async action with busy/error state; resets when `open` changes. */
function useAction(open: boolean) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    setBusy(false);
    setError(null);
  }, [open]);
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function CurrentPasswordField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Vault password">
      {(p) => (
        <SecretInput
          {...p}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="current-password"
        />
      )}
    </Field>
  );
}

export function RevealSecretModal({ open, onOpenChange }: DialogProps) {
  const mode = useConfig().data?.networkMode ?? "testnet";
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState<VaultSecret | null>(null);
  const { busy, error, run } = useAction(open);

  useEffect(() => {
    // Forget the password and the secret when the dialog closes.
    setPassword("");
    setSecret(null);
  }, [open]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      setSecret(await revealVaultSecret(password));
      setPassword("");
    });
  };

  const isPhrase = secret?.kind === "mnemonic";
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isPhrase || !secret ? "Your recovery phrase / keys" : "Your private keys"}
      description="Make sure nobody can see your screen."
    >
      {!secret ? (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <Notice tone="warning">
            Anyone who sees these can take your funds. Never share them, and never type them into a website.
          </Notice>
          <CurrentPasswordField value={password} onChange={setPassword} />
          {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
          <Button type="submit" block loading={busy} disabled={!password}>
            Show
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          {secret.kind === "mnemonic" ? (
            <WordGrid words={secret.mnemonic.split(" ")} />
          ) : (
            <ul className="flex flex-col gap-3">
              {KEY_FAMILIES.filter((f) => secret.keys[f]).map((f) => (
                <li key={f} className="rounded-field border border-line bg-soft p-3">
                  <p className="mb-1 text-xs text-muted">{FAMILY_NAME[f]} private key</p>
                  <code
                    className="block font-mono text-sm break-all text-fg"
                    data-testid={`private-key-${f}`}
                  >
                    {exportPrivateKey(f, secret.keys[f]!, mode)}
                  </code>
                </li>
              ))}
            </ul>
          )}
          <Button variant="secondary" block onClick={() => onOpenChange(false)}>
            Hide
          </Button>
        </div>
      )}
    </Modal>
  );
}

export function ChangePasswordModal({ open, onOpenChange }: DialogProps) {
  const changePassword = useVault((s) => s.changePassword);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState(false);
  const { busy, error, run } = useAction(open);

  useEffect(() => {
    setCurrent("");
    setNext(null);
    setTouched(false);
    setDone(false);
  }, [open]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!next || !current) return;
    void run(async () => {
      await changePassword(current, next);
      setDone(true);
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Change vault password">
      {done ? (
        <div className="flex flex-col gap-4">
          <Notice tone="success">Password changed. Use the new one next time you unlock.</Notice>
          <Button block onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <CurrentPasswordField value={current} onChange={setCurrent} />
          {/* Remount on open so the new-password fields start empty. */}
          <NewPasswordFields key={String(open)} onChange={setNext} touched={touched} label="New password" />
          {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
          <Button type="submit" block loading={busy}>
            Change password
          </Button>
        </form>
      )}
    </Modal>
  );
}

export function AddKeyModal({ open, onOpenChange }: DialogProps) {
  const config = useConfig();
  const mode = config.data?.networkMode ?? "testnet";
  const families = useVault((s) => s.families);
  const addKey = useVault((s) => s.addKey);
  const missing = KEY_FAMILIES.filter((f) => !families.includes(f));
  const [family, setFamily] = useState<KeyFamily>(missing[0] ?? "EVM");
  const [key, setKey] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, run } = useAction(open);

  useEffect(() => {
    setFamily(missing[0] ?? "EVM");
    setKey("");
    setPassword("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await addKey(password, family, key);
      onOpenChange(false);
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add a private key"
      description="For a chain the vault doesn't cover yet."
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Chain" hint={familyChains(config.data?.chains ?? [], family)}>
          {(p) => (
            <Select {...p} value={family} onChange={(e) => setFamily(e.target.value as KeyFamily)}>
              {missing.map((f) => (
                <option key={f} value={f}>
                  {FAMILY_NAME[f]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Private key" hint={privateKeyHint(family, mode)}>
          {(p) => (
            <SecretInput
              {...p}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              autoComplete="off"
              className="font-mono"
            />
          )}
        </Field>
        <CurrentPasswordField value={password} onChange={setPassword} />
        {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
        <Button type="submit" block loading={busy} disabled={!key.trim() || !password}>
          Add key
        </Button>
      </form>
    </Modal>
  );
}

export function RemoveVaultModal({ open, onOpenChange }: DialogProps) {
  const remove = useVault((s) => s.remove);
  const [typed, setTyped] = useState("");
  const [backedUp, setBackedUp] = useState(false);
  const { busy, error, run } = useAction(open);

  useEffect(() => {
    setTyped("");
    setBackedUp(false);
  }, [open]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await remove();
      onOpenChange(false);
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Remove vault from this device">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Notice tone="danger" title="Your funds stay on the blockchain">
          …but after removing, only your recovery phrase or private keys can reach them again. Without a
          backup they are lost for good.
        </Notice>
        <label className="flex min-h-11 items-center gap-3 text-sm text-fg">
          <input
            type="checkbox"
            className="size-5 accent-[var(--danger)]"
            checked={backedUp}
            onChange={(e) => setBackedUp(e.target.checked)}
          />
          I have my recovery phrase or private keys written down.
        </label>
        <Field label='Type "REMOVE" to confirm'>
          {(p) => (
            <Input
              {...p}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          )}
        </Field>
        {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
        <Button
          type="submit"
          variant="danger"
          block
          loading={busy}
          disabled={!backedUp || typed !== "REMOVE"}
        >
          Remove vault
        </Button>
      </form>
    </Modal>
  );
}
